import { inject, Injectable, signal } from '@angular/core';
import { BancolombiaServiceService } from './bancolombia-service.service';
import { SesionService } from 'src/app/core/services/sesion.service';

@Injectable({
  providedIn: 'root',
})
export class ConnectStateService {
  private readonly bancolombiaService = inject(BancolombiaServiceService);
  private readonly sesion = inject(SesionService);

  /**  que almacena el estado de navegación actual de todos los componentes. 
   * Los componentes pueden reaccionar automáticamente a los cambios de su valor.
   */
  
  //flow state
  public flowState = signal<string>('Inicio');

  /** Indica si el flujo activo es el de Bancolombia. */
  public isBancolombiaFlow = signal<boolean>(false);

  /** 
   * Almacén auxiliar interno para el último paso o punto de control exitoso del usuario. 
   * Así, si abandona o cierra la ventana, podemos recordar en qué paso exacto se quedó y 
   * enviarlo al registro de telemetría o backend. 
   */
  private lastValidState = signal<string>('Inicio');

  /**
   * Razón de abandono pendiente de consumir. Se setea antes de navegar fuera del flujo
   * (ej: cierre de sesión) para que el template lo lea en el evento NavigationStart.
   */
  private _pendingAbandonmentReason: string | null = null;

  /** 
   * Canal de Comunicación Distribuido (API nativa BroadcastChannel).
   * Permite enviar datos a TODO el navegador local que comparta el mismo origen (dominio).
   * Así sincroniza la SPA sin importar en cuántas pestañas se divida el proceso.
   */
  private channel = new BroadcastChannel('flujo_channel');

  /**
   * Constructor del Servicio.
   * Inicializa el 'listener' del BroadcastChannel para reaccionar asíncronamente
   * a todos los mensajes disparados por los componentes hijos en otras pestañas.
   */
  constructor() { 
    // Escucha eventos desde otras pestañas/ventanas
    this.channel.onmessage = (event) => {
      if (event.data.type === 'ESTADO_UPDATE') {
        const newState = event.data.estado;
        this.flowState.set(newState);
        
        // Protegemos nuestro 'checkpoint' si el mensaje entrante no es un evento destructivo/abandono.
        if (!newState.toLowerCase().includes('abandonado')) {
          this.lastValidState.set(newState);
        }
      }
    };
  }

  /**
   * Dispara una actualización reactiva del estado actual y avisa a todas las pestañas 
   * oyentes conectadas a este dominio.
   * @param newState El texto/estado descriptivo actual. Ejemplo: "En Componente Hijo 1".
   * @param emit Booleano; dicta si el cambio debe propagarse a través del BroadcastChannel 
   * para informar al resto del navegador o mantenerse local, por defecto `true`.
   */
  
  updateState(newState: string, emit: boolean = true) {
    this.flowState.set(newState);
    
    // Guardamos el último estado que no sea de abandono
    if (!newState.toLowerCase().includes('abandonado')) {
      this.lastValidState.set(newState);
    }
    
    // Avisar a otras ventanas mediante PostMessage
    if (emit) {
      this.channel.postMessage({ type: 'ESTADO_UPDATE', estado: newState });
    }
  }

  //este metodo es el que se llama desde los componentes, y a su vez llama a updateState 
  // para hacer la lógica de actualización y emisión.
  updateFlowState(newState: string, emit: boolean = true) {
    this.updateState(newState, emit);
  }

  /**
   * Aborta forzadamente el flujo en todo el ecosistema de pestañas que escuchan.
   * Calcula en base al punto de control cuál fue el último punto tocado, y propaga 
   * globalmente el evento de término de sesión fallida.
   */
  abandonmentFlow() {
    const lastState = this.lastValidState();
    const abandonedState = `Abandonado en: ${lastState}`;
    
    // Usamos updateState para que se emita por el BroadcastChannel y todos lo reciban
    this.updateState(abandonedState);
  }

  /**
   * Retorna imperativamente el valor string que la Signal retiene en memoria.
   * Útil para procesos fuera del ciclo de cambio de estado.
   * @returns El estado guardado.
   */
  getStatus() {
    return this.flowState();
  }

  /**
   * Registra una razón de abandono que será consumida una sola vez por el template
   * al detectar la navegación de salida. Se usa cuando la salida del flujo es
   * disparada externamente (ej: cierre de sesión desde el header).
   * @param reason Razón exacta a enviar al backend (ej: 'End sesión')
   */
  setPendingAbandonmentReason(reason: string): void {
    this._pendingAbandonmentReason = reason;
  }

  /**
   * Lee y borra la razón de abandono pendiente. Devuelve null si no hay ninguna.
   * Patrón consume-una-vez para evitar que el mismo reason se envíe dos veces.
   */
  consumePendingAbandonmentReason(): string | null {
    const reason = this._pendingAbandonmentReason;
    this._pendingAbandonmentReason = null;
    return reason;
  }
}
